// ═══════════════════════════════════════════════════════════════
// MONTHLY GIVERS REPORT
// Path: src/components/reports/MonthlyGiversReport.jsx
//
// Detects likely monthly/recurring donors from gift history,
// regardless of the is_recurring flag on the donor record.
//
// Detection rule:
//   - 9+ gifts in the trailing 12 months
//   - Median gap between consecutive gifts is 25–40 days
//
// Highlights donors who match detection but have not been flagged,
// so staff can mark them as recurring without leaving the report.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Repeat, Download, CheckCircle2, AlertTriangle } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { Card } from "../ui/Card";
import { supabase } from "../../lib/supabase";

const MIN_GIFTS = 9;
const MIN_MEDIAN_GAP = 25;
const MAX_MEDIAN_GAP = 40;

const fmtCurrency = (amount) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(amount || 0);

const median = (values) => {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2
    ? sorted[mid]
    : (sorted[mid - 1] + sorted[mid]) / 2;
};

export const MonthlyGiversReport = () => {
  const { palette, members, updateMember, dataVersion } = useApp();

  const [gifts, setGifts] = useState([]);
  const [loading, setLoading] = useState(true);

  // 13-month window — one month of lookback past the 12mo detection window
  const { windowStart, windowEnd } = useMemo(() => {
    const end = new Date();
    const start = new Date();
    start.setMonth(start.getMonth() - 13);
    return {
      windowStart: start.toISOString().slice(0, 10),
      windowEnd: end.toISOString().slice(0, 10),
    };
  }, []);

  const loadGifts = useCallback(async () => {
    setLoading(true);
    try {
      let allGifts = [];
      let lastId = null;
      const pageSize = 1000;
      while (true) {
        let query = supabase
          .from("gifts")
          .select("id, member_id, amount, gift_date")
          .gte("gift_date", windowStart)
          .lte("gift_date", windowEnd)
          .order("id", { ascending: true })
          .limit(pageSize);
        if (lastId) query = query.gt("id", lastId);
        const { data, error } = await query;
        if (error) throw error;
        if (!data || data.length === 0) break;
        allGifts = allGifts.concat(data);
        lastId = data[data.length - 1].id;
        if (data.length < pageSize) break;
      }
      setGifts(allGifts);
    } catch (err) {
      console.error("MonthlyGiversReport load error:", err);
    } finally {
      setLoading(false);
    }
  }, [windowStart, windowEnd, dataVersion]);

  useEffect(() => {
    loadGifts();
  }, [loadGifts]);

  // Aggregate gifts by donor, compute detection within 12mo window
  const detected = useMemo(() => {
    const twelveMoAgo = new Date();
    twelveMoAgo.setMonth(twelveMoAgo.getMonth() - 12);
    const cutoff = twelveMoAgo.toISOString().slice(0, 10);

    const byMember = {};
    gifts.forEach((g) => {
      if (!g.member_id || !g.gift_date) return;
      if (g.gift_date < cutoff) return;
      if (!byMember[g.member_id]) byMember[g.member_id] = [];
      byMember[g.member_id].push({
        date: g.gift_date,
        amount: parseFloat(g.amount) || 0,
      });
    });

    const rows = [];
    Object.entries(byMember).forEach(([memberId, list]) => {
      if (list.length < MIN_GIFTS) return;

      const sorted = [...list].sort((a, b) => a.date.localeCompare(b.date));
      const gaps = [];
      for (let i = 1; i < sorted.length; i++) {
        const prev = new Date(sorted[i - 1].date);
        const cur = new Date(sorted[i].date);
        gaps.push((cur - prev) / 86400000);
      }
      const medianGap = median(gaps);
      if (medianGap < MIN_MEDIAN_GAP || medianGap > MAX_MEDIAN_GAP) return;

      const totalAmount = sorted.reduce((s, g) => s + g.amount, 0);
      const avgAmount = totalAmount / sorted.length;
      const lastGift = sorted[sorted.length - 1].date;

      const member = members.find((m) => m.id === memberId);
      if (!member) return;

      rows.push({
        memberId,
        name: `${member.lastName || ""}, ${member.firstName || ""}`
          .trim()
          .replace(/^,\s*/, "")
          .replace(/,\s*$/, ""),
        firstName: member.firstName || "",
        lastName: member.lastName || "",
        email: member.email || "",
        organization: member.organization || "",
        giftCount: sorted.length,
        medianGap: Math.round(medianGap),
        avgAmount,
        totalAmount,
        lastGift,
        isFlagged: !!member.isRecurring,
      });
    });

    return rows.sort((a, b) => {
      if (a.isFlagged !== b.isFlagged) return a.isFlagged ? 1 : -1;
      return b.totalAmount - a.totalAmount;
    });
  }, [gifts, members]);

  const stats = useMemo(() => {
    const total = detected.length;
    const flagged = detected.filter((r) => r.isFlagged).length;
    const missing = total - flagged;
    const totalGivingFlagged = members.filter((m) => m.isRecurring).length;
    return { total, flagged, missing, totalGivingFlagged };
  }, [detected, members]);

  const handleMarkRecurring = async (memberId) => {
    try {
      await updateMember(memberId, { isRecurring: true }, true);
    } catch (err) {
      console.error("MonthlyGiversReport mark recurring error:", err);
    }
  };

  const handleExport = () => {
    if (!detected.length) return;
    const headers = [
      "Last Name",
      "First Name",
      "Organization",
      "Email",
      "Gifts (12mo)",
      "Median Gap (days)",
      "Average Gift",
      "Total (12mo)",
      "Last Gift",
      "Flagged",
    ];
    const rows = detected.map((r) => [
      `"${r.lastName}"`,
      `"${r.firstName}"`,
      `"${r.organization}"`,
      `"${r.email}"`,
      r.giftCount,
      r.medianGap,
      r.avgAmount.toFixed(2),
      r.totalAmount.toFixed(2),
      r.lastGift,
      r.isFlagged ? "Yes" : "No",
    ]);
    const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `monthly-givers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const th = {
    padding: "8px 10px",
    textAlign: "left",
    fontSize: "0.65rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: palette.text3,
    borderBottom: `2px solid ${palette.cardBorder}`,
    background: palette.cardBg,
  };

  const td = {
    padding: "7px 10px",
    fontSize: FONT_SIZES.xs,
    fontFamily: FONT_FAMILY,
    borderBottom: `1px solid ${palette.cardBorder}`,
    color: palette.text1,
  };

  const StatBox = ({ label, value, color }) => (
    <Card style={{ textAlign: "center", padding: 16, flex: 1 }}>
      <div
        style={{
          fontSize: "1.8rem",
          fontWeight: 700,
          color: color || palette.accent1,
          lineHeight: 1,
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: FONT_SIZES.sm,
          fontWeight: 600,
          color: palette.text1,
          marginTop: 6,
        }}
      >
        {label}
      </div>
    </Card>
  );

  return (
    <div>
      {/* Intro */}
      <Card style={{ marginBottom: 16, padding: 14 }}>
        <div
          style={{
            display: "flex",
            gap: 10,
            alignItems: "flex-start",
          }}
        >
          <Repeat size={ICON_SIZES.md} style={{ color: palette.accent1, flexShrink: 0, marginTop: 2 }} />
          <div style={{ fontSize: FONT_SIZES.sm, color: palette.text2, lineHeight: 1.5 }}>
            Donors with <strong>{MIN_GIFTS}+ gifts in the last 12 months</strong>{" "}
            and a median gap of <strong>{MIN_MEDIAN_GAP}–{MAX_MEDIAN_GAP} days</strong>{" "}
            between gifts. This catches active monthly givers even if they have
            not been manually flagged as <em>Recurring</em> on their record.
          </div>
        </div>
      </Card>

      {/* Summary stats */}
      <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
        <StatBox label="Detected" value={stats.total} />
        <StatBox label="Already Flagged" value={stats.flagged} color="#10b981" />
        <StatBox
          label="Missing Flag"
          value={stats.missing}
          color={stats.missing > 0 ? "#ef4444" : "#9ca3af"}
        />
      </div>

      {/* Table */}
      <Card style={{ padding: 0, overflow: "hidden" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "12px 14px",
            borderBottom: `1px solid ${palette.cardBorder}`,
          }}
        >
          <div
            style={{
              fontSize: FONT_SIZES.sm,
              fontWeight: 600,
              color: palette.text1,
            }}
          >
            {loading
              ? "Loading gift history…"
              : `${detected.length} likely monthly donor${detected.length === 1 ? "" : "s"}`}
          </div>
          <button
            onClick={handleExport}
            disabled={!detected.length}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 14px",
              borderRadius: 8,
              border: `1px solid ${palette.accent1}`,
              background: `${palette.accent1}08`,
              color: palette.accent1,
              fontSize: FONT_SIZES.xs,
              fontWeight: 600,
              fontFamily: FONT_FAMILY,
              cursor: detected.length ? "pointer" : "not-allowed",
              opacity: detected.length ? 1 : 0.5,
            }}
          >
            <Download size={ICON_SIZES.sm} /> Export CSV
          </button>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontFamily: FONT_FAMILY,
            }}
          >
            <thead>
              <tr>
                <th style={th}>Donor</th>
                <th style={th}>Organization</th>
                <th style={{ ...th, textAlign: "right" }}>Gifts</th>
                <th style={{ ...th, textAlign: "right" }}>Median Gap</th>
                <th style={{ ...th, textAlign: "right" }}>Avg Gift</th>
                <th style={{ ...th, textAlign: "right" }}>Total (12mo)</th>
                <th style={th}>Last Gift</th>
                <th style={th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {!loading && detected.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ ...td, textAlign: "center", color: palette.text3, padding: 24 }}>
                    No monthly giving patterns detected in the last 12 months.
                  </td>
                </tr>
              )}
              {detected.map((r) => (
                <tr
                  key={r.memberId}
                  style={{
                    background: r.isFlagged ? "transparent" : `#fef3c7`,
                  }}
                >
                  <td style={{ ...td, fontWeight: 600 }}>{r.name || "—"}</td>
                  <td style={td}>{r.organization || "—"}</td>
                  <td style={{ ...td, textAlign: "right" }}>{r.giftCount}</td>
                  <td style={{ ...td, textAlign: "right" }}>{r.medianGap}d</td>
                  <td style={{ ...td, textAlign: "right" }}>{fmtCurrency(r.avgAmount)}</td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 600 }}>{fmtCurrency(r.totalAmount)}</td>
                  <td style={td}>{r.lastGift}</td>
                  <td style={td}>
                    {r.isFlagged ? (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                          color: "#10b981",
                          fontWeight: 600,
                        }}
                      >
                        <CheckCircle2 size={ICON_SIZES.sm} /> Flagged
                      </span>
                    ) : (
                      <button
                        onClick={() => handleMarkRecurring(r.memberId)}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                          padding: "4px 10px",
                          borderRadius: 6,
                          border: `1px solid #d97706`,
                          background: "#fff",
                          color: "#d97706",
                          fontSize: "0.7rem",
                          fontWeight: 600,
                          fontFamily: FONT_FAMILY,
                          cursor: "pointer",
                        }}
                      >
                        <AlertTriangle size={ICON_SIZES.xs} /> Mark Recurring
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};
